// dependency-graph-ignore-file
import { useCallback, useEffect, useRef } from 'react';

import type { FireParams, MetricConfig, MetricMetadataBase } from './define-metric';
import { fireMetric } from './fire-metric';
import { cancelMetric } from './stabilized-scheduler';

/**
 * React hook for firing a stabilized metric.
 *
 * Manages a MetricRef internally; cancels any in-flight metric on unmount.
 * Returns a `fire` function to trigger the metric with given params.
 *
 * Include `contentId` in `params.metadata` when you need database size fields on the report.
 */
export function useMetric<
  TData,
  TMetadata extends MetricMetadataBase = MetricMetadataBase
>(
  _config: MetricConfig<TData, TMetadata>
): {
  fire: (params: FireParams<TData, TMetadata>) => void;
} {
  const ref = useRef<{ rafId: number } | null>(null);
  const configRef = useRef(_config);
  configRef.current = _config;

  useEffect(() => {
    return () => {
      cancelMetric(ref);
    };
  }, []);

  const fire = useCallback((params: FireParams<TData, TMetadata>) => {
    fireMetric(configRef.current, params, ref);
  }, []);

  return { fire };
}
